import { useEffect, useState } from 'react';

import { getCollectionCollaborators } from '../services/users';
import { normalizeId } from '../utils/ids';
import { onSessionChange } from '../utils/session';

/**
 * The collaborators of a collection — what a `user` element's picker lists
 * first (GET /api/collections/{id}/collaborators): owner, grant holders,
 * group members and the caller, public projection, name-sorted.
 *
 * Fetched ONCE per collection per session and shared by every picker on the
 * page (a multi add form may hold a dozen): the roster changes when sharing
 * changes, which is a different screen. A 403 (a card-scoped grant may not
 * enumerate the roster) or any other failure resolves to an EMPTY list —
 * the picker still finds anyone by exact email, so nothing is blocked.
 *
 * Returns `{ users, loading, error }`; `users` is a stable array per
 * collection.
 */

const EMPTY = Object.freeze([]);
// collectionId -> Promise<users[]>
const cache = new Map();

function load(collectionId) {
	if (cache.has(collectionId)) return cache.get(collectionId);
	const promise = getCollectionCollaborators(collectionId)
		.then(users => Object.freeze((users || []).filter(user => normalizeId(user?._id))))
		.catch(() => {
			// A failure is not remembered: the next mount asks again.
			cache.delete(collectionId);
			return EMPTY;
		});
	cache.set(collectionId, promise);
	return promise;
}

// The roster is what THIS account may enumerate (a card-scoped grantee gets
// none): another account (or none) asks again.
onSessionChange(() => cache.clear());

/** Test seam: forget every collection's roster. */
export function resetCollaboratorsCache() {
	cache.clear();
}

export default function useCollaborators(collectionId) {
	const id = normalizeId(collectionId);
	const [state, setState] = useState({ id: null, users: EMPTY, loading: !!id });

	useEffect(() => {
		if (!id) {
			setState({ id: null, users: EMPTY, loading: false });
			return undefined;
		}
		let active = true;
		setState(prev => (prev.id === id ? prev : { id, users: EMPTY, loading: true }));
		load(id).then(users => {
			if (active) setState({ id, users, loading: false });
		});
		return () => { active = false; };
	}, [id]);

	return {
		users: state.id === id ? state.users : EMPTY,
		loading: !!id && (state.id !== id || state.loading),
	};
}
